import { AdyenCheckout, Dropin, Card, PreAuthorizedDebitCanada } from "@adyen/adyen-web/auto";
import "@adyen/adyen-web/styles/adyen.css";
import { configService } from "./ConfigService";
import { PaymentLogUseCases } from "@/application/use-cases/PaymentLogUseCases";
import { logger } from "./LoggerService";
import i18n from "@/plugins/i18n";
import { normalizeLocaleForAdyen } from "@/shared/utils/localeUtils";
import { ErrorHandler, ERROR_CONTEXTS } from "@/shared/utils/errorHandler";
const PAD_PAYMENT_METHOD = "eft_directdebit_CA";
export class AdyenPaymentService {
    constructor() {
        Object.defineProperty(this, "checkout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "dropinComponent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isDropinConfigured", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "paymentLogUseCases", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "cancelUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "retryUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        // No session service needed - we get session from paymentSession
        this.paymentLogUseCases = new PaymentLogUseCases();
    }
    async initialize(config) {
        try {
            logger.debug("Initializing Adyen with configuration:", config);
            if (!config.paymentSession) {
                throw new Error("Payment session is required for Sessions Flow");
            }
            const configAdyen = configService.adyen;
            if (!configAdyen.clientKey) {
                throw new Error("Client key is required for Adyen initialization");
            }
            logger.debug("Environment:", configAdyen.environment);
            logger.debug("Client key:", configAdyen.clientKey);
            logger.debug("Using session from backend:", config.paymentSession.sessionId);
            // Cleanup existing checkout and dropin if reinitializing
            if (this.dropinComponent) {
                try {
                    logger.debug("Cleaning up existing dropin component before reinitialization");
                    this.dropinComponent = null;
                    this.isDropinConfigured = false;
                }
                catch (error) {
                    logger.debug("Error cleaning up dropin component:", error);
                }
            }
            if (this.checkout) {
                try {
                    logger.debug("Cleaning up existing checkout before reinitialization");
                    this.checkout = null;
                }
                catch (error) {
                    logger.debug("Error cleaning up checkout:", error);
                }
            }
            // Store URLs for error handling
            this.cancelUrl = config.paymentDetails?.urlCancelReturn || null;
            // Retry URL will be the current payment page (we'll get it from window.location)
            this.retryUrl = typeof window !== "undefined" ? window.location.pathname : null;
            // Initialize Adyen Checkout with Sessions Flow configuration
            // For Sessions Flow, we need to configure the session properly
            // Use unified locale normalization for consistent locale handling
            const normalizedLocale = normalizeLocaleForAdyen(config.locale);
            const checkoutConfig = {
                environment: configAdyen.environment,
                clientKey: configAdyen.clientKey,
                session: {
                    id: config.paymentSession.sessionId,
                    sessionData: config.paymentSession.sessionData,
                },
                locale: normalizedLocale,
                countryCode: config.countryCode || "FR",
                onPaymentCompleted: (result, component) => {
                    logger.debug("Payment completed:", result);
                    this.handlePaymentCompleted(result, component);
                },
                onPaymentFailed: (result, component) => {
                    logger.debug("Payment failed:", result);
                    this.handlePaymentFailed(result, component);
                },
                onError: (error, component) => {
                    logger.debug("Payment error:", error);
                    this.handlePaymentError(error, component);
                },
                // Handle SEPA Direct Debit confirmation before submission
                // According to Adyen docs: https://docs.adyen.com/online-payments/build-your-integration/sessions-flow
                // beforeSubmit(data, component, actions) - must call actions.resolve(data) to continue with payment data
                beforeSubmit: async (data, component, actions) => {
                    logger.debug("Checkout beforeSubmit triggered", {
                        type: data.paymentMethod?.type,
                        isValid: component.isValid,
                        data: data,
                    });
                    // Only intercept SEPA Direct Debit payments
                    if (data.paymentMethod?.type === "sepadirectdebit") {
                        logger.debug("SEPA Direct Debit payment detected, requesting user confirmation");
                        try {
                            // Create a promise that will be resolved by user action
                            const userConfirmed = await new Promise((resolve) => {
                                // Dispatch custom event to show confirmation dialog
                                window.dispatchEvent(new CustomEvent("adyen-sepa-confirmation-required", {
                                    detail: {
                                        onConfirm: () => {
                                            logger.debug("User confirmed SEPA payment");
                                            resolve(true);
                                        },
                                        onCancel: () => {
                                            logger.debug("User cancelled SEPA payment");
                                            resolve(false);
                                        },
                                    },
                                }));
                            });
                            if (!userConfirmed) {
                                logger.debug("SEPA payment cancelled by user, stopping payment flow");
                                // Stop the payment flow - this resets the button state
                                actions.reject();
                                return;
                            }
                            logger.debug("SEPA payment confirmed, continuing payment flow with data:", data);
                            // Continue the payment flow WITH the payment data
                            // This is critical: actions.resolve(data) passes the paymentMethod to Adyen
                            actions.resolve(data);
                        }
                        catch (error) {
                            logger.debug("Error during SEPA confirmation:", error);
                            // Stop the payment flow on error
                            actions.reject();
                        }
                    }
                    else if (data.paymentMethod?.type === PAD_PAYMENT_METHOD) {
                        logger.debug("PAD (Pre-Authorized Debit Canada) payment detected, requesting user confirmation");
                        try {
                            const userConfirmed = await new Promise((resolve) => {
                                window.dispatchEvent(new CustomEvent("adyen-pad-confirmation-required", {
                                    detail: {
                                        onConfirm: () => {
                                            logger.debug("User confirmed PAD payment");
                                            resolve(true);
                                        },
                                        onCancel: () => {
                                            logger.debug("User cancelled PAD payment");
                                            resolve(false);
                                        },
                                    },
                                }));
                            });
                            if (!userConfirmed) {
                                logger.debug("PAD payment cancelled by user, stopping payment flow");
                                actions.reject();
                                return;
                            }
                            logger.debug("PAD payment confirmed, continuing payment flow with data:", data);
                            actions.resolve(data);
                        }
                        catch (error) {
                            // R47: silent catch (actions.reject() shows no user-facing toast) → must be logger.error.
                            logger.error("Error during PAD confirmation:", error);
                            actions.reject();
                        }
                    }
                    else {
                        // For other payment methods, continue immediately with the payment data
                        actions.resolve(data);
                    }
                },
            };
            logger.debug("Checkout config for Sessions Flow:", checkoutConfig);
            this.checkout = await AdyenCheckout(checkoutConfig);
            this.isInitialized = true;
            logger.debug("Adyen checkout initialized successfully with Sessions Flow");
        }
        catch (error) {
            logger.debug("Failed to initialize Adyen:", error);
            this.isInitialized = false;
            this.checkout = null;
            throw new Error(`Failed to initialize payment system: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
    }
    async handlePaymentCompleted(result, component) {
        // Log payment success to backend
        await this.paymentLogUseCases.logPaymentSuccess(result, component);
        // Emit custom event for payment completion
        window.dispatchEvent(new CustomEvent("adyen-payment-completed", {
            detail: { result, component },
        }));
    }
    handlePaymentFailed(result, component) {
        // Log payment failure to backend
        this.paymentLogUseCases.logPaymentFailed(result, component);
        // Emit custom event for payment failure
        window.dispatchEvent(new CustomEvent("adyen-payment-failed", {
            detail: { result, component },
        }));
        // Check if this is a payment refused error (bank declined)
        // Common Adyen result codes for refused payments: Refused, Declined, Error, Cancelled
        const resultCode = result?.resultCode || "";
        const refusedCodes = ["Refused", "Error", "Cancelled"];
        const isRefused = refusedCodes.includes(resultCode);
        if (isRefused) {
            // Use specialized payment refused handler with cancel and retry URLs
            logger.debug(`Payment refused with resultCode: ${resultCode}`);
            ErrorHandler.handlePaymentRefused(this.cancelUrl, this.retryUrl);
        }
        else {
            // Use the generic error handler for other payment failures
            logger.debug(`Payment failed with unexpected resultCode: ${resultCode}`);
            const errorMessage = resultCode || "Payment failed";
            ErrorHandler.handleApiError({ message: errorMessage, details: result }, ERROR_CONTEXTS.PAYMENT_PROCESSING);
        }
    }
    handlePaymentError(error, component) {
        // Log payment error to backend
        this.paymentLogUseCases.logPaymentError(error, component);
        // Emit custom event for payment error
        window.dispatchEvent(new CustomEvent("adyen-payment-error", {
            detail: { error, component },
        }));
        // Determine if we should navigate to error page based on error type
        // Only IMPLEMENTATION_ERROR is considered non-recoverable and requires navigation
        // Other errors (NETWORK_ERROR, CANCEL, ERROR) are recoverable and should not navigate
        const errorName = error?.name || "";
        const shouldNavigate = errorName === "IMPLEMENTATION_ERROR";
        // Use the Adyen error handler with conditional navigation
        ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_PROCESSING, shouldNavigate);
    }
    isReady() {
        return this.isInitialized && this.checkout !== null;
    }
    getDropInComponent() {
        if (!this.isReady()) {
            throw new Error("Adyen is not initialized");
        }
        if (!this.dropinComponent) {
            // Create Drop-in component with Sessions Flow configuration
            // For Sessions Flow, we create the dropin from the checkout instance
            logger.debug("Creating drop-in component for Sessions Flow...");
            const dropinConfiguration = {
                // Required if you import individual payment methods.
                paymentMethodComponents: [Card, PreAuthorizedDebitCanada],
                // Optional configuration.
                onReady: () => {
                    logger.debug("Drop-in component ready");
                },
                paymentMethodsConfiguration: {
                    card: {
                        name: i18n.global.t("adyen.paymentMethods.card"),
                        // Optional configuration.
                        hasHolderName: true, // Show the cardholder name field.
                        holderNameRequired: true, // Mark the cardholder name field as required.
                        onConfigSuccess: () => {
                            logger.debug("Card secured fields configured successfully");
                            this.isDropinConfigured = true;
                        },
                    },
                    paybybank: {
                        name: i18n.global.t("adyen.paymentMethods.paybybank"),
                    },
                    sepadirectdebit: {
                        name: i18n.global.t("adyen.paymentMethods.sepadirectdebit"),
                        // SEPA confirmation is now handled at checkout level via onSubmit
                        // This provides better control over button state
                    },
                    [PAD_PAYMENT_METHOD]: {
                        enableStoreDetails: true,
                    },
                },
            };
            this.dropinComponent = new Dropin(this.checkout, dropinConfiguration);
            logger.debug("Drop-in component created successfully for Sessions Flow");
        }
        return this.dropinComponent;
    }
    isDropinReady() {
        return this.isDropinConfigured;
    }
    async processPayment(paymentData) {
        try {
            // Process payment with Adyen
            // This would typically involve calling your backend
            logger.debug("Processing payment:", paymentData);
            return paymentData;
        }
        catch (error) {
            logger.debug("Payment processing failed:", error);
            throw new Error("Payment processing failed");
        }
    }
    // Handle redirect results (for 3D Secure, etc.)
    async handleRedirectResult(redirectResult) {
        if (!this.isReady()) {
            throw new Error("Adyen is not initialized");
        }
        try {
            return await this.checkout.submitDetails({
                details: { redirectResult },
            });
        }
        catch (error) {
            logger.debug("Failed to handle redirect result:", error);
            throw new Error("Failed to handle redirect result");
        }
    }
}
